import type {AudioBufferSink} from 'mediabunny';

export type ZeroCrossingSearchResult = {
    index: number;
    hasExactCrossing: boolean;
};

export async function readMonoWindowFromSink(sink: AudioBufferSink, startSec: number, durationSec: number, sampleRate?: number): Promise<Float32Array | null> {
    const sr = sampleRate || 48000;
    const requestedStartSec = Math.max(0, Number(startSec) || 0);
    const requestedDurationSec = Math.max(0, Number(durationSec) || 0);
    const requestedEndSec = requestedStartSec + requestedDurationSec;
    const framesNeeded = Math.max(1, Math.floor(requestedDurationSec * sr));
    const iter = sink.buffers(requestedStartSec, requestedEndSec);
    let mono: Float32Array | null = null;
    let copiedFrames = 0;
    let maxWrittenFrame = 0;
    
    for await (const {buffer, timestamp, duration} of iter as any) {
        if (!buffer) break;
        const bufferSampleRate = Number.isFinite(buffer?.sampleRate) ? Number(buffer.sampleRate) : sr;
        const bufferStartSec = Number.isFinite(timestamp) ? Number(timestamp) : requestedStartSec;
        const bufferDurationSec = Number.isFinite(duration) && Number(duration) > 0
            ? Number(duration)
            : buffer.length / bufferSampleRate;
        const bufferEndSec = bufferStartSec + bufferDurationSec;

        const overlapStartSec = Math.max(requestedStartSec, bufferStartSec);
        const overlapEndSec = Math.min(requestedEndSec, bufferEndSec);
        if (!(overlapEndSec > overlapStartSec)) continue;

        const dstStart = Math.max(0, Math.floor((overlapStartSec - requestedStartSec) * sr));
        const dstEnd = Math.min(framesNeeded, Math.ceil((overlapEndSec - requestedStartSec) * sr));
        const srcStart = Math.max(0, Math.floor((overlapStartSec - bufferStartSec) * bufferSampleRate));
        const srcEnd = Math.min(buffer.length, Math.ceil((overlapEndSec - bufferStartSec) * bufferSampleRate));
        const take = Math.min(dstEnd - dstStart, srcEnd - srcStart);
        if (take <= 0) continue;

        const channels = buffer.numberOfChannels;
        const chunk = new Float32Array(take);

        // Downmix to mono by averaging channels
        for (let ch = 0; ch < channels; ch++) {
            const data = buffer.getChannelData(ch);
            for (let i = 0; i < take; i++) chunk[i] += data[srcStart + i] / channels;
        }

        if (!mono) mono = new Float32Array(framesNeeded);
        mono.set(chunk, dstStart);
        copiedFrames += take;
        maxWrittenFrame = Math.max(maxWrittenFrame, dstStart + take);
        if (maxWrittenFrame >= framesNeeded) break;
    }
    try { await (iter as any)?.return?.(); } catch {}

    if (!mono || copiedFrames <= 0) {
        console.log("FOUND NOTHING reading audio buffer sink");
        return null;
    }


    return mono;
}

export function findNearestZeroCross(samples: Float32Array, centerIndex: number): ZeroCrossingSearchResult | null {
    const n = samples.length;
    const radius = Math.min(centerIndex, n - 1 - centerIndex);
    let bestIdx: number | null = null;
    let bestDist = Number.POSITIVE_INFINITY;
    for (let r = 0; r <= radius; r++) {
        const i1 = centerIndex - r;
        const i2 = i1 + 1;
        if (i1 < 0 || i2 >= n) continue;
        const s1 = samples[i1];
        const s2 = samples[i2];
        if (s1 === 0) return { index: i1, hasExactCrossing: true };
        if ((s1 < 0 && s2 > 0) || (s1 > 0 && s2 < 0)) {
            const t = Math.abs(s1) / (Math.abs(s1) + Math.abs(s2));
            const z = i1 + t;
            const dist = Math.abs(z - centerIndex);
            if (dist < bestDist) { bestDist = dist; bestIdx = z; }
        }
    }
    if (bestIdx == null) {
        let idx = centerIndex;
        let minAbs = Math.abs(samples[idx]);
        const radius2 = Math.min(200, Math.max(centerIndex, n - centerIndex));
        for (let r = 1; r <= radius2; r++) {
            const left = centerIndex - r;
            const right = centerIndex + r;
            if (left >= 0 && Math.abs(samples[left]) < minAbs) { idx = left; minAbs = Math.abs(samples[left]); }
            if (right < n && Math.abs(samples[right]) < minAbs) { idx = right; minAbs = Math.abs(samples[right]); }
        }
        return { index: idx, hasExactCrossing: false };
    }
    return { index: (bestIdx as number) | 0, hasExactCrossing: true };
}

export function rms(samples: Float32Array, start: number, len: number): number {
    const n = samples.length;
    if (start < 0) start = 0;
    if (start >= n) return 0;
    const end = Math.min(n, start + Math.max(1, len));
    let sum = 0;
    for (let i = start; i < end; i++) sum += samples[i] * samples[i];
    return Math.sqrt(sum / (end - start));
}

export async function analyzeSegmentJoinsWithSink(
    sink: AudioBufferSink,
    segments: Array<{ srcStartSec: number; srcEndSec: number; compStartSec: number; fadeInMs?: number; fadeOutMs?: number }>,
    sampleRate?: number
): Promise<void> {
    if (!segments || segments.length <= 1) return;
    const sr = sampleRate || 48000;
    const searchWindowSec = 0.02;
    const halfWin = searchWindowSec / 2;
    const rmsWinSec = 0.01;
    const baseMs = 10;
    const maxSnap = 0.005;

    for (let i = 1; i < segments.length; i++) {
        const prev = segments[i - 1];
        const curr = segments[i];
        const boundarySrcEnd = prev.srcEndSec ?? 0;
        const boundarySrcStart = curr.srcStartSec ?? 0;

        const leftMono = await readMonoWindowFromSink(sink, boundarySrcEnd - halfWin, searchWindowSec, sr);
        const rightMono = await readMonoWindowFromSink(sink, boundarySrcStart - halfWin, searchWindowSec, sr);
        if (!leftMono || !rightMono) continue;

        const leftCross = findNearestZeroCross(leftMono, Math.floor(leftMono.length / 2));
        const rightCross = findNearestZeroCross(rightMono, Math.floor(rightMono.length / 2));

        let snappedLeftSec = boundarySrcEnd;
        let snappedRightSec = boundarySrcStart;
        if (leftCross != null) snappedLeftSec = (boundarySrcEnd - halfWin) + (leftCross.index / sr);
        if (rightCross != null) snappedRightSec = (boundarySrcStart - halfWin) + (rightCross.index / sr);

        const leftDelta = Math.max(-maxSnap, Math.min(maxSnap, snappedLeftSec - (prev.srcEndSec ?? snappedLeftSec)));
        const rightDelta = Math.max(-maxSnap, Math.min(maxSnap, snappedRightSec - (curr.srcStartSec ?? snappedRightSec)));
        prev.srcEndSec = Math.max(prev.srcStartSec, (prev.srcEndSec ?? 0) + leftDelta);
        curr.srcStartSec = Math.min(curr.srcEndSec, (curr.srcStartSec ?? 0) + rightDelta);

        const rmsLeft = rms(leftMono, Math.max(0, Math.floor(leftMono.length / 2 - rmsWinSec * sr)), Math.floor(rmsWinSec * sr));
        const rmsRight = rms(rightMono, Math.floor(rightMono.length / 2), Math.floor(rmsWinSec * sr));
        const ratio = (Math.max(rmsLeft, 1e-6)) > 0 ? (rmsRight / Math.max(rmsLeft, 1e-6)) : 1;

        let fadeMs = baseMs;
        if (!Number.isFinite(ratio)) {
            fadeMs = 20;
        } else if (ratio > 2 || ratio < 0.5) {
            fadeMs = 20;
        }
        if (!leftCross?.hasExactCrossing || !rightCross?.hasExactCrossing) fadeMs = Math.max(fadeMs, 20);

        prev.fadeOutMs = Math.max(prev.fadeOutMs ?? 0, fadeMs);
        curr.fadeInMs = Math.max(curr.fadeInMs ?? 0, fadeMs);
    }
}
